import { ES_MAPPING_KEYS } from '@/components/EsSheet/data';
import { TablePagination } from '@/components/shared/TablePagination';
import { abyssColors } from '@/theme/colors';
import { getNextValidStep } from '../shared/validation';
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  Grow,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import { useAtom } from 'jotai';
import { useEffect, useMemo, useState } from 'react';
import { useWizard } from '../context';
import * as state from '@/components/Analysis/state';
import { Banner } from '@/components/shared/Banner';
import { EsSheetData } from '../shared/UploadPanel/UploadPanel';
import { PanelHeader } from '../styles';
import { ColumnBox, FieldRow, InputBox, MappedColumnPaper, StyledSelect } from './styles';

// Extended version of EsSheetData to include mappings
type ExtendedEsSheetData = EsSheetData & {
  mappings?: Record<string, string>;
};

type ColumnMapping = {
  sourceColumn: string;
  targetColumn: string;
  isRequired: boolean;
  isValid: boolean;
};

export const MapColumns = () => {
  const [originalEsSheet] = useAtom(state.originalEsSheet);
  const [editableEsSheet, setEditableEsSheet] = useAtom<ExtendedEsSheetData | null>(
    state.editableEsSheet
  );
  const [columnMappings, setColumnMappings] = useState<ColumnMapping[]>([]);
  const [isInitialized, setIsInitialized] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const { goToPreviousStep, goToStep } = useWizard();

  // Initialize column mappings from existing mappings in editableEsSheet
  useEffect(() => {
    if (!originalEsSheet || !editableEsSheet || isInitialized) return;

    const existingMappings = editableEsSheet.mappings || {};

    const initialMappings: ColumnMapping[] = originalEsSheet.headers.map((header) => {
      const targetKey = existingMappings[header] || '';
      const targetMapping = ES_MAPPING_KEYS.find((key) => key.key === targetKey);

      return {
        sourceColumn: header,
        targetColumn: targetKey,
        isRequired: targetMapping?.required || false,
        isValid: !!targetKey,
      };
    });

    setColumnMappings(initialMappings);
    setIsInitialized(true);
  }, [originalEsSheet, editableEsSheet, isInitialized]);

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  // Calculate mapped and unmapped counts
  const { totalCount, mappedCount, unmappedCount } = useMemo(() => {
    const total = columnMappings.length;
    const mapped = columnMappings.filter((mapping) => mapping.isValid).length;
    const unmapped = total - mapped;

    return {
      totalCount: total,
      mappedCount: mapped,
      unmappedCount: unmapped,
    };
  }, [columnMappings]);

  // Check if all required fields are mapped
  const areAllRequiredFieldsMapped = useMemo(() => {
    const requiredFields = ES_MAPPING_KEYS.filter((key) => key.required);

    // Check if all required ES fields have been mapped to at least one source column
    return requiredFields.every((requiredField) =>
      columnMappings.some(
        (mapping) => mapping.targetColumn === requiredField.key && mapping.isValid
      )
    );
  }, [columnMappings]);

  // Update the editable data when mappings change
  const handleMappingChange = (sourceIndex: number, targetKey: string) => {
    if (!editableEsSheet) return;

    const newMappings = [...columnMappings];
    const targetMapping = ES_MAPPING_KEYS.find((mapping) => mapping.key === targetKey);

    newMappings[sourceIndex] = {
      ...newMappings[sourceIndex],
      targetColumn: targetKey,
      isRequired: targetMapping?.required || false,
      isValid: !!targetKey,
    };

    setColumnMappings(newMappings);

    // Store column mappings as lookup reference (sourceColumn -> targetKey)
    const mappings = newMappings.reduce((acc, mapping) => {
      if (mapping.targetColumn) {
        acc[mapping.sourceColumn] = mapping.targetColumn;
      }
      return acc;
    }, {} as Record<string, string>);

    setEditableEsSheet({
      ...editableEsSheet,
      mappings,
      valueMappings: undefined,
    } as ExtendedEsSheetData);
  };

  if (!originalEsSheet || !editableEsSheet) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h5" color="error">
          No data available. Please upload a file first.
        </Typography>
      </Box>
    );
  }

  return (
    <Stack width="100%" minWidth="85.4rem" paddingBottom="6.4rem" gap="1.6rem">
      <Stack gap="3.2rem">
        <PanelHeader>
          <Typography variant="h3" fontWeight={500} color="primary.600">
            Map Columns to ES Fields
          </Typography>
          <Typography variant="h5" fontWeight={400} color="primary.400">
            Map each column from your file to the corresponding ES Tool field
          </Typography>
        </PanelHeader>

        <Grow in={unmappedCount > 0} timeout={300} style={{ transitionDelay: '100ms' }}>
          <div>
            <Banner
              message="Some fields or values need to be mapped before you can continue."
              severity="warning"
            />
          </div>
        </Grow>
      </Stack>
      <Stack width="100%" justifyContent="center" direction="row">
        <MappedColumnPaper elevation={0}>
          <Stack spacing={3}>
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={2} marginBottom="1.6rem">
                <Grid size={6}>
                  <Typography variant="h6" color="primary.600">
                    Columns from your file
                  </Typography>
                </Grid>
                <Grid size={6}>
                  <Typography variant="h6" color="primary.600">
                    Map Columns to ES Fields
                  </Typography>
                </Grid>
              </Grid>
              <Stack direction="row" spacing="2.4rem" marginBottom="2.4rem">
                <Typography variant="body2" color="primary.600">
                  {totalCount} total {totalCount === 1 ? 'column' : 'columns'} found
                </Typography>
                <Typography variant="body2" color="success.600">
                  {mappedCount} mapped
                </Typography>
                <Typography variant="body2" color="error.600">
                  {unmappedCount.toString().padStart(2, '0')} un-mapped
                </Typography>
              </Stack>
              <Stack gap="1.6rem">
                {columnMappings
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((mapping, displayIndex) => {
                    const actualIndex = page * rowsPerPage + displayIndex;
                    return (
                      <FieldRow key={actualIndex}>
                        <InputBox>
                          <ColumnBox>
                            <Typography variant="caption">{mapping.sourceColumn}</Typography>
                          </ColumnBox>
                        </InputBox>
                        <InputBox>
                          <FormControl
                            fullWidth
                            error={mapping.isRequired && !mapping.isValid}
                            size="small"
                          >
                            <StyledSelect
                              value={mapping.targetColumn}
                              onChange={(e) =>
                                handleMappingChange(actualIndex, e.target.value as string)
                              }
                            >
                              <MenuItem value="">-- Not Mapped --</MenuItem>
                              {ES_MAPPING_KEYS.map((key) => (
                                <MenuItem
                                  key={key.key}
                                  value={key.key}
                                  disabled={columnMappings.some(
                                    (m) =>
                                      m.targetColumn === key.key &&
                                      m.sourceColumn !== mapping.sourceColumn
                                  )}
                                >
                                  {key.name}
                                  {key.required ? (
                                    <span style={{ color: abyssColors.error[500] }}> * </span>
                                  ) : (
                                    ''
                                  )}
                                </MenuItem>
                              ))}
                            </StyledSelect>
                            {mapping.isRequired && !mapping.isValid && (
                              <FormHelperText>This field is required</FormHelperText>
                            )}
                          </FormControl>
                        </InputBox>
                      </FieldRow>
                    );
                  })}
              </Stack>
            </Box>
          </Stack>
        </MappedColumnPaper>
      </Stack>
      <Stack direction="row" justifyContent="space-between" alignItems="center" width="100%">
        <TablePagination
          count={columnMappings.length}
          page={page}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[
            { label: '5', value: 5 },
            { label: '10', value: 10 },
            { label: '25', value: 25 },
            { label: 'All', value: -1 },
          ]}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
        />
        <Stack direction="row" gap={2}>
          <Button
            variant="outlined"
            onClick={goToPreviousStep}
            sx={{
              textTransform: 'none',
              borderColor: abyssColors.primary[300],
              color: abyssColors.primary[600],
              '&:hover': {
                borderColor: abyssColors.primary[400],
                backgroundColor: abyssColors.primary[50],
              },
            }}
          >
            Previous
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              if (!editableEsSheet) return;
              // Smart navigation: skip to next step that needs attention
              const nextStep = getNextValidStep(editableEsSheet);
              goToStep(nextStep);
            }}
            disabled={!areAllRequiredFieldsMapped}
            sx={{
              textTransform: 'none',
            }}
          >
            Next
          </Button>
        </Stack>
      </Stack>
    </Stack>
  );
};
